import { useEffect, useMemo, useState } from "react";
import { Trophy, Medal, CalendarBlank, ChartLineUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";

const KU_GROUPS = ["KU A", "KU B", "KU C", "KU D", "Junior", "Senior", "Tanpa Data"];

function kuGroupOf(athlete) {
    // Pakai nilai KU yang sama persis dengan yang tertulis di data Siswa Aktif
    // (dihitung backend dari tahun lahir: KU A 2019+, KU B 2017-2018, KU C 2015-2016,
    // KU D 2012-2014, Junior 2008-2011, Senior 2007 ke bawah)
    return (athlete.ku || "").trim() || "Tanpa Data";
}

function genderOf(gender) {
    const value = (gender || "").trim().toLowerCase();
    if (["l", "laki-laki", "putra"].includes(value)) return "Putra";
    if (["p", "perempuan", "putri"].includes(value)) return "Putri";
    return "";
}

const RANK_STYLES = [
    "bg-racing-gold/20 border-racing-gold text-racing-golddeep",
    "bg-neutral-200/60 border-neutral-300 text-neutral-700",
    "bg-orange-100 border-orange-300 text-orange-700",
];

export default function RankingsTab() {
    const [athletes, setAthletes] = useState([]);
    const [records, setRecords] = useState([]);
    const [raceNumber, setRaceNumber] = useState("");
    const [mode, setMode] = useState("pb");
    const [sessionDate, setSessionDate] = useState("");
    const [classFilter, setClassFilter] = useState("");
    const [kuFilter, setKuFilter] = useState("");

    useEffect(() => {
        Promise.all([api.get("/coach/athletes"), api.get("/time-records")])
            .then(([athletesRes, recordsRes]) => {
                setAthletes(athletesRes.data);
                setRecords(recordsRes.data);
            })
            .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));
    }, []);

    const athleteMap = useMemo(() => Object.fromEntries(athletes.map((a) => [a.id, a])), [athletes]);

    const raceOptions = useMemo(
        () => [...new Set(records.map((r) => (r.race_number || "").trim()).filter(Boolean))].sort(),
        [records]
    );

    const classOptions = useMemo(
        () => [...new Set(athletes.map((a) => a.class_name).filter(Boolean))].sort(),
        [athletes]
    );

    const sessionDates = useMemo(() => {
        if (!raceNumber) return [];
        return [...new Set(records.filter((r) => (r.race_number || "").trim() === raceNumber).map((r) => r.record_date))]
            .filter(Boolean)
            .sort()
            .reverse();
    }, [records, raceNumber]);

    useEffect(() => {
        setSessionDate("");
    }, [raceNumber, mode]);

    const ranking = useMemo(() => {
        if (!raceNumber) return [];
        let scoped = records.filter((r) => (r.race_number || "").trim() === raceNumber);
        if (mode === "session") {
            if (!sessionDate) return [];
            scoped = scoped.filter((r) => r.record_date === sessionDate);
        }
        const bestByAthlete = {};
        scoped.forEach((r) => {
            const current = bestByAthlete[r.athlete_id];
            if (!current || (r.time_ms || Infinity) < (current.time_ms || Infinity)) {
                bestByAthlete[r.athlete_id] = r;
            }
        });
        const rows = Object.values(bestByAthlete)
            .map((r) => {
                const athlete = athleteMap[r.athlete_id] || {};
                return {
                    ...r,
                    gender: genderOf(athlete.gender),
                    ku_group: kuGroupOf(athlete),
                    class_name: r.class_name || athlete.class_name || "",
                };
            })
            .filter((r) => (!classFilter || r.class_name === classFilter) && (!kuFilter || r.ku_group === kuFilter))
            .sort((a, b) => (a.time_ms || 0) - (b.time_ms || 0));
        return KU_GROUPS.map((group) => ({
            group,
            putri: rows.filter((r) => r.ku_group === group && r.gender === "Putri"),
            putra: rows.filter((r) => r.ku_group === group && r.gender === "Putra"),
        })).filter((g) => g.putri.length > 0 || g.putra.length > 0);
    }, [records, raceNumber, mode, sessionDate, athleteMap, classFilter, kuFilter]);

    const renderTable = (rows, genderLabel, key) => (
        <div key={key} className="border border-racing-line" data-testid={`ranking-table-${key}`}>
            <p className={`px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] border-b border-racing-line ${genderLabel === "Putri" ? "bg-racing-red/5 text-racing-red" : "bg-neutral-100 text-neutral-700"}`}>
                {genderLabel} • {rows.length} atlet
            </p>
            {rows.map((row, index) => (
                <div key={row.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 border-b border-racing-line last:border-0" data-testid={`ranking-row-${key}-${index}`}>
                    <span className={`w-9 h-9 flex items-center justify-center border font-display text-lg ${RANK_STYLES[index] || "border-racing-line text-racing-muted"}`} data-testid={`ranking-rank-${key}-${index}`}>
                        {index + 1}
                    </span>
                    <span className="flex-1 min-w-[160px]">
                        <span className="block font-medium text-sm text-neutral-900">{row.athlete_name}</span>
                        <span className="block text-[10px] uppercase tracking-[0.15em] text-racing-muted">{row.class_name || "—"}</span>
                    </span>
                    <span className="font-display text-xl text-racing-red" data-testid={`ranking-time-${key}-${index}`}>{row.time_text}</span>
                    <span className="text-xs text-racing-muted">{row.record_date}</span>
                    {row.note && <span className="w-full text-xs text-racing-muted italic">&ldquo;{row.note}&rdquo;</span>}
                </div>
            ))}
        </div>
    );

    return (
        <div className="space-y-8" data-testid="rankings-tab">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <h2 className="font-display uppercase text-2xl flex items-center gap-3"><Trophy size={26} className="text-racing-golddeep" /> Ranking Catatan Waktu</h2>
                    <p className="text-sm text-racing-muted mt-2">Analisis peringkat atlet per kelompok umur (KU D sampai Senior), dipisah Putri dan Putra. Pilih kelas tertentu atau bandingkan seluruh kelas sekaligus — berdasarkan Personal Best keseluruhan atau satu sesi tanggal tertentu.</p>
                </div>
            </div>

            <div className="bg-white border border-racing-line p-5 flex flex-wrap items-end gap-4" data-testid="ranking-controls">
                <div className="min-w-[200px]">
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Nomor Lomba</label>
                    <select value={raceNumber} onChange={(e) => setRaceNumber(e.target.value)} data-testid="ranking-race-select" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red">
                        <option value="">Pilih nomor lomba...</option>
                        {raceOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Dasar Ranking</label>
                    <div className="flex border border-racing-line" data-testid="ranking-mode-toggle">
                        <button
                            type="button"
                            onClick={() => setMode("pb")}
                            data-testid="ranking-mode-pb"
                            className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.15em] transition-colors ${mode === "pb" ? "bg-racing-red text-white" : "bg-white text-racing-muted hover:text-racing-red"}`}
                        >
                            <Medal size={15} /> Personal Best
                        </button>
                        <button
                            type="button"
                            onClick={() => setMode("session")}
                            data-testid="ranking-mode-session"
                            className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-[0.15em] border-l border-racing-line transition-colors ${mode === "session" ? "bg-racing-red text-white" : "bg-white text-racing-muted hover:text-racing-red"}`}
                        >
                            <CalendarBlank size={15} /> Sesi Tanggal
                        </button>
                    </div>
                </div>
                {mode === "session" && (
                    <div className="min-w-[200px]">
                        <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Tanggal Sesi</label>
                        <select value={sessionDate} onChange={(e) => setSessionDate(e.target.value)} data-testid="ranking-date-select" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red">
                            <option value="">Pilih tanggal...</option>
                            {sessionDates.map((date) => <option key={date} value={date}>{date}</option>)}
                        </select>
                    </div>
                )}
                <div className="min-w-[190px]">
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Kelas</label>
                    <select value={classFilter} onChange={(e) => setClassFilter(e.target.value)} data-testid="ranking-class-select" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red">
                        <option value="">Semua Kelas (bandingkan)</option>
                        {classOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                </div>
                <div className="min-w-[190px]">
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Kelompok Umur</label>
                    <select value={kuFilter} onChange={(e) => setKuFilter(e.target.value)} data-testid="ranking-ku-select" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red">
                        <option value="">Semua KU</option>
                        {KU_GROUPS.filter((g) => g !== "Tanpa Data").map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                </div>
            </div>

            {!raceNumber && (
                <p className="border border-dashed border-racing-line px-4 py-8 text-center text-racing-muted" data-testid="ranking-empty">Pilih nomor lomba untuk melihat ranking.</p>
            )}
            {raceNumber && mode === "session" && !sessionDate && (
                <p className="border border-dashed border-racing-line px-4 py-8 text-center text-racing-muted" data-testid="ranking-empty-date">Pilih tanggal sesi pencatatan.</p>
            )}
            {raceNumber && (mode === "pb" || sessionDate) && ranking.length === 0 && (
                <p className="border border-dashed border-racing-line px-4 py-8 text-center text-racing-muted" data-testid="ranking-no-data">Belum ada catatan waktu untuk pilihan ini.</p>
            )}

            <div className="space-y-8" data-testid="ranking-results">
                {ranking.map((group, groupIndex) => (
                    <section key={group.group} data-testid={`ranking-group-${groupIndex}`}>
                        <h3 className="font-display uppercase text-xl flex items-center gap-3 mb-3">
                            <ChartLineUp size={20} className="text-racing-red" /> {group.group === "Tanpa Data" ? "Tanpa Data Usia" : `Kelompok Umur ${group.group}`}
                            <span className="text-xs font-sans normal-case tracking-normal text-racing-muted">({group.putri.length + group.putra.length} atlet)</span>
                        </h3>
                        <div className="grid lg:grid-cols-2 gap-4">
                            {group.putri.length > 0 && renderTable(group.putri, "Putri", `${groupIndex}-putri`)}
                            {group.putra.length > 0 && renderTable(group.putra, "Putra", `${groupIndex}-putra`)}
                        </div>
                    </section>
                ))}
            </div>
        </div>
    );
}
